import { formatAmount } from "@utils";
import { InvoiceGroup } from "@components";

const LedgerPayment = ({ payment }) => {
  if (!payment) return null;

  const allTransactionsAtTimeOfPayment =
    payment?.ledgerTransactionsAtTimeOfPayment || [];
  const paidTransactions = payment?.allocations || [];

  const paymentDate = new Date(payment.paymentDate);
  const paymentMonth = paymentDate.getMonth();
  const paymentYear = paymentDate.getFullYear();

  const isOverdue = (dueDate) => {
    const date = new Date(dueDate);
    const dueMonth = date.getMonth();
    const dueYear = date.getFullYear();
    return (
      dueYear < paymentYear ||
      (dueYear === paymentYear && dueMonth < paymentMonth)
    );
  };

  // Determine if a bucket type was requested based on payment.invoicesRequested
  const isBucketRequested = (bucketType) => {
    const invoicesRequested = payment?.invoicesRequested;

    switch (invoicesRequested) {
      case "Any":
        return true;
      case "Overdue":
        return bucketType === "overdue";
      case "Overdue + due this month":
        return bucketType === "overdue" || bucketType === "dueThisMonth";
      case "All":
        return true;
      default:
        return false;
    }
  };

  // Create a map of paid transactions by ledgerTransactionId
  const paidTransactionsMap = new Map();
  paidTransactions.forEach((transaction) => {
    paidTransactionsMap.set(transaction.ledgerTransactionId, {
      ...transaction,
      isPaid: true,
    });
  });

  // Merge: replace transactions in allTransactionsAtTimeOfPayment with paid ones
  const mergedTransactions = allTransactionsAtTimeOfPayment.map((transaction) =>
    paidTransactionsMap.has(transaction.ledgerTransactionId)
      ? paidTransactionsMap.get(transaction.ledgerTransactionId)
      : transaction
  );

  // Group allocations
  const overdueInvoices = [];
  const dueThisMonthInvoices = [];
  const futureInvoiceBuckets = new Map();
  const otherTransactions = [];
  const creditNotesMap = new Map();

  mergedTransactions.forEach((transaction) => {
    if (
      transaction.transactionType === "CreditNote" &&
      transaction.transactionOriginalTransactionNumber
    ) {
      // Store credit notes with original transaction numbers for later association
      const existingCreditNotes =
        creditNotesMap.get(transaction.transactionOriginalTransactionNumber) ||
        [];
      existingCreditNotes.push(transaction);
      creditNotesMap.set(
        transaction.transactionOriginalTransactionNumber,
        existingCreditNotes
      );
    } else if (
      transaction.transactionType === "Invoice" ||
      transaction.transactionType === "CreditNote"
    ) {
      const invoice = { ...transaction };
      const dueDate = new Date(transaction.transactionDueDate);
      const dueMonth = dueDate.getMonth();
      const dueYear = dueDate.getFullYear();
      if (isOverdue(transaction.transactionDueDate)) {
        overdueInvoices.push(invoice);
      } else if (dueYear === paymentYear && dueMonth === paymentMonth) {
        dueThisMonthInvoices.push(invoice);
      } else {
        const bucketKey = `${dueYear}-${dueMonth}`;
        const bucket = futureInvoiceBuckets.get(bucketKey) ?? [];
        bucket.push(invoice);
        futureInvoiceBuckets.set(bucketKey, bucket);
      }
    } else {
      otherTransactions.push(transaction);
    }
  });

  // Associate credit notes with their invoices
  const associateCreditNotes = (invoices) => {
    return invoices.map((invoice) => ({
      ...invoice,
      associatedCreditNotes:
        creditNotesMap.get(invoice.transactionNumber) || [],
    }));
  };

  const shouldShowBucket = (bucketType, invoices) => {
    const isRequested = isBucketRequested(bucketType);
    const hasPaidInvoices = invoices.some((invoice) => invoice.isPaid);
    return isRequested || hasPaidInvoices;
  };

  const byDueDate = (a, b) =>
    new Date(a.transactionDueDate) - new Date(b.transactionDueDate);

  const processedOverdueInvoices = associateCreditNotes(overdueInvoices).sort(byDueDate);
  const processedDueThisMonthInvoices = associateCreditNotes(dueThisMonthInvoices).sort(byDueDate);
  const processedFutureBuckets = Array.from(futureInvoiceBuckets.entries())
    .sort((a, b) => {
      const [yearA, monthA] = a[0].split("-").map(Number);
      const [yearB, monthB] = b[0].split("-").map(Number);
      if (yearA !== yearB) return yearA - yearB;
      return monthA - monthB;
    })
    .map(([key, invoices]) => {
      const [dueYear, dueMonth] = key.split("-").map(Number);
      const monthLabel = new Date(dueYear, dueMonth).toLocaleString("en", {
        month: "short",
      });
      return {
        key,
        name: `Due in ${monthLabel}`,
        invoices: associateCreditNotes(invoices).sort(byDueDate),
      };
    });

  return (
    <>
      {(processedOverdueInvoices.length > 0 ||
        processedDueThisMonthInvoices.length > 0 ||
        processedFutureBuckets.length > 0 ||
        otherTransactions.length > 0) && (
        <div className="px-5 py-5 border-b">
          <div className="w-full flex mb-4 items-center">
            <p className="font-semibold m-0">Invoices</p>
          </div>
          <div className="flex flex-col gap-2">
            {processedOverdueInvoices.length > 0 &&
              shouldShowBucket("overdue", processedOverdueInvoices) && (
                <InvoiceGroup
                  name="Overdue"
                  invoices={processedOverdueInvoices}
                  isRequested={isBucketRequested("overdue")}
                />
              )}
            {processedDueThisMonthInvoices.length > 0 &&
              shouldShowBucket(
                "dueThisMonth",
                processedDueThisMonthInvoices
              ) && (
                <InvoiceGroup
                  name="Due this month"
                  invoices={processedDueThisMonthInvoices}
                  isRequested={isBucketRequested("dueThisMonth")}
                />
              )}
            {processedFutureBuckets.map(
              (bucket) =>
                shouldShowBucket("future", bucket.invoices) && (
                  <InvoiceGroup
                    key={bucket.key}
                    name={bucket.name}
                    invoices={bucket.invoices}
                    isRequested={isBucketRequested("future")}
                  />
                )
            )}
            {otherTransactions.length > 0 && (
              <div className="relative z-0 border border-zinc-200 rounded-xl overflow-hidden bg-white shadow-[0px_1px_4px_0px_rgba(0,0,0,0.10)] select-none">
                <div className="flex items-start px-5 py-4 relative z-10 w-full">
                  <div className="flex flex-col items-start gap-1 grow">
                    <span className="font-medium">
                      Unallocated cash applied
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="flex flex-col items-end gap-1">
                      <span className="font-medium text-right text-emerald-700">
                        {formatAmount(
                          otherTransactions.reduce(
                            (sum, t) => sum + t.amount,
                            0
                          )
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default LedgerPayment;
